import { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Heading,
  Text,
  Link as ChakraLink,
  Button,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';

import mailImg from '../../../assets/auth/mail.svg';
import { Input } from '../../../components';
import {
  displayNotification,
  useGetAuthConfigurationQuery,
  useLoginMutation,
} from '../../../services';
import { LoginData } from '../../../models';
// Canonical envelope helpers — see src/utils/api-response.ts for what the
// global baseQueryWithReAuth layer already reports, and why `.unwrap()` cannot
// be trusted here.
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
} from '../../../utils/api-response';

// core-api answers an invalid login with HTTP 200 and reports the failure
// INSIDE the body. `.unwrap()` only rejects on the transport shape, so the old
// `const { accessToken, refreshToken } = response.data` "failed" purely by
// accident: a `data: null` body threw on destructure, and a `data: ''` body
// destructured to undefined and sailed on — success toast, navigate('/'), and
// the literal string "undefined" written to localStorage as both tokens, which
// then poisoned every subsequent request. Classify the body instead.

/**
 * HTTP 400/500 transport errors are ALREADY surfaced by baseQueryWithReAuth,
 * which toasts its own fixed generic copy for them. Firing a second
 * notification just overwrites that — the notification slice holds ONE
 * notification at a time — so those stay suppressed here.
 *
 * BODY failures are ours alone. As of TICKET-011 the global layer SUPPRESSES
 * its errorCode-1030 toast on the anonymous auth endpoints, this page's
 * `company/authenticate/login` included, because the server's own text there is
 * an account-enumeration oracle. So a 1030 login body now arrives with NOTHING
 * reported: if this page also stayed quiet the user would be told nothing at
 * all. Every body failure — 1030, 1027, `data: ''`, `data: null`, an unknown
 * future code — is reported here, always with the fixed constant below.
 */
const GLOBALLY_TOASTED_HTTP_STATUSES = [400, 500];

// An unauthenticated sign-in form is exactly where an account-enumeration
// oracle must not be opened, so EVERY failure branch renders this one constant:
// never `payload.message`, never `error.data.message`. core-api already funnels
// "no such user" and "wrong password" into the same reply, and this keeps that
// true no matter what the server starts saying later. (Same stance admin-portal
// takes on its own sign-in form.)
const GENERIC_LOGIN_FAILURE = 'Please check your credentials and try again';

const transportStatus = (response: unknown): unknown =>
  (response as { error?: { status?: unknown } })?.error?.status;

const isAlreadyReportedGlobally = (
  response: unknown,
  outcome: string
): boolean => {
  if (outcome === MutationResult.TRANSPORT_FAILURE) {
    return GLOBALLY_TOASTED_HTTP_STATUSES.includes(
      Number(transportStatus(response))
    );
  }
  // Body failures (1030 included) are not globally toasted on this endpoint.
  return false;
};

export const Login = () => {
  const [loginTrials, setLoginTrials] = useState(1);
  const [login] = useLoginMutation();
  const navigate = useNavigate();
  const { data } = useGetAuthConfigurationQuery();

  const emailValidation = data?.data
    ? data?.data?.EMAIL_LOGIN_REGEX?.value
    : undefined;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
  } = useForm<LoginData>();

  const { email } = watch();

  const [isLoginLoading, setIsLoading] = useState(false);

  // Single exit for every non-success path: stop the spinner, never persist,
  // never navigate, keep the typed email on screen and put the cursor back in
  // it. `alreadyReported` suppresses only the duplicate toast, never the stop.
  const failLogin = (description: string, alreadyReported: boolean) => {
    setIsLoading(false);

    if (!alreadyReported) {
      displayNotification({
        title: 'Login Failed',
        description,
        status: 'error',
      });
    }

    setFocus('email');
  };

  const onSubmit: SubmitHandler<LoginData> = async loginData => {
    setIsLoading(true);
    setLoginTrials(loginTrials + 1);

    try {
      // No `.unwrap()`: it resolves for a body-level failure and throws away
      // the envelope we have to branch on. Await the trigger and read the body.
      const response = await login(loginData);
      const outcome = classifyMutationResult(response);

      if (outcome !== MutationResult.SUCCESS || !('data' in response)) {
        const payload = extractResponsePayload(response);
        // The server's text is logged for diagnosis (errorCode only, never the
        // message) and NEVER rendered.
        console.error('Login rejected', {
          outcome,
          errorCode: payload.errorCode,
        });
        failLogin(
          GENERIC_LOGIN_FAILURE,
          isAlreadyReportedGlobally(response, outcome)
        );
        return;
      }

      // A body-level success with a half-built session is still a failure:
      // persisting an undefined token is exactly the bug this ticket closes.
      const session = response.data.data as
        | { accessToken?: string; refreshToken?: string }
        | null
        | undefined;

      if (!session?.accessToken || !session?.refreshToken) {
        console.error('Login succeeded with an incomplete token pair');
        failLogin(GENERIC_LOGIN_FAILURE, false);
        return;
      }

      localStorage.setItem('accessToken', session.accessToken);
      localStorage.setItem('refreshToken', session.refreshToken);

      displayNotification({
        title: 'Login Successful',
        description: 'You have successfully logged in',
        status: 'success',
      });
      navigate('/');
    } catch (error: any) {
      // Without `.unwrap()` the trigger no longer rejects on a transport error
      // (that arrives as `{ error }` and is classified above), so this is the
      // belt-and-braces path for a genuine throw — serialisation, an abort.
      console.error('Login error:', error);
      failLogin(GENERIC_LOGIN_FAILURE, false);
    }
  };
  return (
    <>
      <Box textAlign="center" w="24rem">
        <Heading as="h3" fontSize="3xl" color="custom_blue.1" mb="12px">
          Sign in
        </Heading>
        <Text fontSize="md" mb="2rem" color="custom_gray.1">
          Use your work email to sign in
        </Text>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl mb="2rem">
            <FormLabel htmlFor="email" fontSize="1rem">
              Email
            </FormLabel>
            <Input
              required
              type="text"
              name="email"
              id="email"
              placeholder="Enter your email"
              register={register}
              prefixIcon={mailImg}
              isError={errors.email?.type === 'pattern'}
              pattern={
                new RegExp(
                  emailValidation
                    ? emailValidation.slice(1, emailValidation.length - 1)
                    : '',
                  'g'
                )
              }
            />
          </FormControl>
          <FormControl mb="1rem">
            <FormLabel htmlFor="password" fontSize="1rem">
              Password
            </FormLabel>
            <Input
              required
              type="password"
              name="password"
              id="password"
              register={register}
            />
          </FormControl>
          <Box textAlign="right">
            {/* NOTE: use Chakra Link with React-router-DOM Link & check the frequency of using this component to create Atom  */}
            <ChakraLink
              as={Link}
              fontWeight="bold"
              state={email}
              color="custom_orange.2"
              to="/forget-password"
            >
              Forget password
            </ChakraLink>
          </Box>
          {loginTrials > 3 && (
            <>
              <Box mt="3" bg="beige" p="6">
                <Text fontSize="sm" fontWeight="semibold" color="custom_blue.1">
                  It happens! we can help you
                </Text>
                <Text fontSize="sm" color="custom_blue.1">
                  Instead of trying more, you can just reset your password by
                  clicking on{' '}
                  <ChakraLink
                    as={Link}
                    to="/forget-password"
                    state={email}
                    color="custom_orange.2"
                    textDecoration="underline"
                  >
                    Forget Password
                  </ChakraLink>
                </Text>
              </Box>
            </>
          )}
          <Box w="full" mt="6">
            <Button
              w="full"
              type="submit"
              isLoading={isLoginLoading}
              color="white"
              fontWeight="semibold"
              bg="custom_orange.2"
              _hover={{
                bg: 'custom_orange.2',
              }}
            >
              Sign in
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );
};
